import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PRICING } from "@/data/content";

const EMPTY = { name: "", email: "", phone: "", service_interest: "", organisation: "", message: "" };

export const EnquiryForm = ({
  kind = "consultation",
  showService = true,
  showOrganisation = false,
  showScheduling = false,
  buttonLabel = "Send enquiry",
  testIdPrefix = "enquiry",
}) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (showScheduling && (!date || !slot)) {
      toast.error("Please choose the day and time you'd prefer a call back.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/enquiries", {
        kind,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service_interest: form.service_interest || null,
        organisation: showOrganisation ? form.organisation || null : null,
        message: form.message,
        preferred_date: showScheduling && date ? date : null,
        preferred_slot: showScheduling ? slot || null : null,
      });
      toast.success(data.message);
      setForm(EMPTY);
      setDate("");
      setSlot("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "h-12 rounded-xl border-sand bg-white/80 text-ink placeholder:text-pebble/50 focus-visible:ring-sage";

  return (
    <form data-testid={`${testIdPrefix}-form`} onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testIdPrefix}-name`} className="mb-2 block text-sm font-semibold text-forest">Your name *</label>
          <Input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="e.g. Margaret Fraser" className={fieldClass} />
        </div>
        <div>
          <label htmlFor={`${testIdPrefix}-email`} className="mb-2 block text-sm font-semibold text-forest">Email address *</label>
          <Input id={`${testIdPrefix}-email`} data-testid={`${testIdPrefix}-email-input`} required type="email" value={form.email} onChange={set("email")} placeholder="you@example.co.uk" className={fieldClass} />
        </div>
        <div>
          <label htmlFor={`${testIdPrefix}-phone`} className="mb-2 block text-sm font-semibold text-forest">Phone (optional)</label>
          <Input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} type="tel" value={form.phone} onChange={set("phone")} placeholder="0141 ..." className={fieldClass} />
        </div>
        {showService && (
          <div>
            <label htmlFor={`${testIdPrefix}-service`} className="mb-2 block text-sm font-semibold text-forest">I'm interested in</label>
            <select
              id={`${testIdPrefix}-service`}
              data-testid={`${testIdPrefix}-service-select`}
              value={form.service_interest}
              onChange={set("service_interest")}
              className="h-12 w-full rounded-xl border border-sand bg-white/80 px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage"
            >
              <option value="">Choose a service…</option>
              {PRICING.map((p) => (
                <option key={p.title} value={p.title}>{p.title}</option>
              ))}
              <option value="Something else">Something else / not sure yet</option>
            </select>
          </div>
        )}
        {showOrganisation && (
          <div>
            <label htmlFor={`${testIdPrefix}-organisation`} className="mb-2 block text-sm font-semibold text-forest">Organisation</label>
            <Input id={`${testIdPrefix}-organisation`} data-testid={`${testIdPrefix}-organisation-input`} value={form.organisation} onChange={set("organisation")} placeholder="e.g. NHS GGC, care home, charity" className={fieldClass} />
          </div>
        )}
      </div>
      <div>
        <label htmlFor={`${testIdPrefix}-message`} className="mb-2 block text-sm font-semibold text-forest">Tell us a little about what you need *</label>
        <Textarea
          id={`${testIdPrefix}-message`}
          data-testid={`${testIdPrefix}-message-input`}
          required
          minLength={5}
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Who is this for, whereabouts in Glasgow, and what would a good visit look like?"
          className="rounded-xl border-sand bg-white/80 text-ink placeholder:text-pebble/50 focus-visible:ring-sage"
        />
      </div>
      {showScheduling && (
        <div className="rounded-2xl border border-sage/30 bg-sage-tint/40 p-5 sm:p-6" data-testid={`${testIdPrefix}-scheduling`}>
          <p className="flex items-center gap-2 text-sm font-semibold text-forest">
            <CalendarDays className="h-4 w-4 text-terracotta" /> When would you prefer a call back?
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${testIdPrefix}-date`} className="mb-2 block text-xs font-semibold uppercase tracking-widest text-pebble">Preferred day</label>
              <Input
                id={`${testIdPrefix}-date`}
                data-testid={`${testIdPrefix}-date-input`}
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-12 rounded-xl border-sand bg-white/80 text-ink focus-visible:ring-sage"
              />
            </div>
            <div>
              <label htmlFor={`${testIdPrefix}-time`} className="mb-2 block text-xs font-semibold uppercase tracking-widest text-pebble">Preferred time</label>
              <Input
                id={`${testIdPrefix}-time`}
                data-testid={`${testIdPrefix}-time-input`}
                type="time"
                min="09:00"
                max="20:00"
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                className="h-12 rounded-xl border-sand bg-white/80 text-ink focus-visible:ring-sage"
              />
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-pebble/70">
            Choose whatever suits you, between 9am and 8pm, any day of the week — we'll get back to you to confirm whether we can make that day and time, or suggest the nearest alternative.
          </p>
        </div>
      )}
      <Button
        data-testid={`${testIdPrefix}-submit-button`}
        type="submit"
        disabled={sending}
        className="w-full rounded-full bg-forest py-6 text-base text-linen shadow-[0_12px_28px_rgba(28,56,41,0.25)] transition-colors duration-300 hover:bg-terracotta sm:w-auto sm:px-10"
      >
        {sending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          buttonLabel
        )}
      </Button>
      <p className="text-xs text-pebble/70">We reply within one working day. Your details are only ever used to respond to your enquiry.</p>
    </form>
  );
};
